import React from 'react';

/**
 * TextField - Single-line text input field
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} [props.field.placeholder] - Placeholder text
 * @param {string} [props.field.default] - Default value
 * @param {string} [props.field.description] - Optional description text
 * @param {string} props.value - Current value
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.error] - Error message to display
 * @param {boolean} [props.compact] - Use compact inline layout
 */
export default function TextField({ field, value, onChange, disabled = false, error, compact = false }) {
  const handleChange = (e) => {
    onChange(e.target.value);
  };

  const inputElement = (
    <input
      type="text"
      value={value ?? ''}
      onChange={handleChange}
      disabled={disabled}
      placeholder={field.placeholder || field.default || ''}
      className={`w-full px-2 py-1 text-xs border rounded bg-gray-50 ${
        error
          ? 'border-red-500 focus:ring-red-500'
          : 'border-gray-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-400'
      } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
    />
  );

  // Compact mode: inline label + control
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
          {field.label}
        </label>
        <div className="flex-1 min-w-0">
          {inputElement}
        </div>
      </div>
    );
  }

  // Standard mode: stacked layout
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">
        {field.label}
      </label>
      {inputElement}
      {field.description && (
        <p className="mt-1 text-xs text-gray-500">{field.description}</p>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
}
